/**
 * Body Insight Template System
 *
 * Template-based content generation for 5 body insight pages.
 * Feature: 017-body-insight-summary
 *
 * Template Selection Pattern:
 * - Page 1: Based on life_stage + symptoms
 * - Page 2: Based on selected struggles
 * - Page 3: Based on primary_goal
 * - Page 4: Based on primary_goal → focus areas
 * - Page 5: Based on primary_goal → timeline phases
 */

import { UserProfile } from '../types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface BodyInsightTemplate {
  id: string;
  page: 1 | 2 | 3 | 4 | 5;
  content: {
    headline?: string;
    body?: string;
    listItems?: string[];
    focusAreas?: FocusArea[];
    timelinePhases?: TimelinePhase[];
  };
  conditions: {
    life_stage?: string;
    primary_goal?: string;
    has_struggles?: boolean;
    struggle_categories?: string[];
  };
}

export interface FocusArea {
  category: 'Movement' | 'Nutrition' | 'Recovery';
  title: string;
  description: string;
  why: string;
  suggestions: string[];
}

export interface TimelinePhase {
  phase: 1 | 2 | 3;
  weeks: string;
  title: string;
  focus: string;
  what_to_expect: string[];
}

// ============================================================================
// PAGE 1: YOUR BODY RIGHT NOW - Templates
// ============================================================================

const PAGE1_TEMPLATES: BodyInsightTemplate[] = [
  // Perimenopause variants
  {
    id: 'p1-perimenopause-high-symptoms',
    page: 1,
    content: {
      headline: 'Your Body is Adjusting',
      body: "You're in perimenopause, and your body is going through significant hormonal changes. The symptoms you're experiencing—like hot flashes, mood swings, or sleep disruptions—are your body's way of adapting to fluctuating estrogen and progesterone levels.",
      listItems: [
        'Hormone levels are shifting, not deficient',
        'Your body is finding a new balance',
        'These changes are temporary and manageable',
        'You have more control than you think'
      ]
    },
    conditions: {
      life_stage: 'perimenopause',
      has_struggles: true
    }
  },
  {
    id: 'p1-perimenopause-low-symptoms',
    page: 1,
    content: {
      headline: 'Your Body is Transitioning',
      body: "You're in perimenopause—a natural transition where your body is adjusting to hormonal changes. Even without major symptoms, your body is working behind the scenes to find a new balance.",
      listItems: [
        'Perimenopause can last 4-10 years',
        'Hormone levels fluctuate naturally',
        'Building healthy habits now sets you up for success',
        'You can thrive during this transition'
      ]
    },
    conditions: {
      life_stage: 'perimenopause',
      has_struggles: false
    }
  },
  // Menopause variants
  {
    id: 'p1-menopause-high-symptoms',
    page: 1,
    content: {
      headline: 'Your Body is Recalibrating',
      body: "You're in menopause—defined as 12 months without a period. Your body is stabilizing at new hormone levels. The symptoms you're experiencing are part of this recalibration process, but they don't have to control your life.",
      listItems: [
        'Hormone levels are stabilizing, not broken',
        'Your body is adapting to a new normal',
        'Symptoms typically peak then gradually improve',
        'Targeted lifestyle changes make a big difference'
      ]
    },
    conditions: {
      life_stage: 'menopause',
      has_struggles: true
    }
  },
  {
    id: 'p1-menopause-low-symptoms',
    page: 1,
    content: {
      headline: 'Your Body Has Crossed a Milestone',
      body: "You've reached menopause—12 months without a period. Your body has transitioned to new, stable hormone levels. This is a new chapter with unique opportunities for health and vitality.",
      listItems: [
        'Hormone levels have stabilized',
        'You can build strength and energy at any age',
        'This is a time to focus on long-term health',
        'You have the power to shape this next phase'
      ]
    },
    conditions: {
      life_stage: 'menopause',
      has_struggles: false
    }
  },
  // Post-menopause variants
  {
    id: 'p1-postmenopause-high-symptoms',
    page: 1,
    content: {
      headline: 'Your Body is Still Adapting',
      body: "You're in post-menopause, living with stable hormone levels. While some symptoms may persist, your body continues to adapt. Many women find this phase brings clarity and renewed energy.",
      listItems: [
        'Hormone levels are now stable',
        'Persistent symptoms can be managed effectively',
        'Your body is capable of strength and resilience',
        'This phase can bring unexpected vitality'
      ]
    },
    conditions: {
      life_stage: 'post-menopause',
      has_struggles: true
    }
  },
  {
    id: 'p1-postmenopause-low-symptoms',
    page: 1,
    content: {
      headline: 'Your Body Has Found Its New Balance',
      body: "You're in post-menopause with stable hormone levels. This is a powerful phase—many women report feeling more confident, energized, and focused than ever before.",
      listItems: [
        'You have successfully navigated the transition',
        'Stable hormones mean predictable energy',
        'This is a time to optimize health and strength',
        'You can set new goals and achieve them'
      ]
    },
    conditions: {
      life_stage: 'post-menopause',
      has_struggles: false
    }
  }
];

// ============================================================================
// PAGE 2: WHAT YOU'RE FEELING - Templates
// ============================================================================

const PAGE2_TEMPLATES: BodyInsightTemplate[] = [
  // Energy/fatigue focused
  {
    id: 'p2-energy-fatigue',
    page: 2,
    content: {
      headline: 'You\'re Not Alone in Feeling Tired',
      body: "Energy fluctuations are one of the most common struggles during this transition. Your body's changing hormone levels affect metabolism, sleep quality, and cellular energy production. But fatigue doesn't have to be your new normal.",
      listItems: [
        'Hormone changes affect mitochondrial function (your cells\' energy factories)',
        'Poor sleep compounds daytime fatigue',
        'Insulin resistance can cause energy crashes',
        'Strategic movement and nutrition can restore vitality'
      ]
    },
    conditions: {
      struggle_categories: ['energy']
    }
  },
  // Sleep focused
  {
    id: 'p2-sleep-disruption',
    page: 2,
    content: {
      headline: 'Sleep Disruptions Are Real—And Fixable',
      body: "Sleep problems affect up to 60% of women in perimenopause and menopause. Night sweats, racing thoughts, and frequent waking are linked to hormonal fluctuations. Improving sleep is one of the fastest ways to feel better overall.",
      listItems: [
        'Dropping estrogen affects sleep regulation',
        'Night sweats disrupt deep sleep cycles',
        'Cortisol imbalances cause 3am wake-ups',
        'Sleep hygiene and recovery strategies work'
      ]
    },
    conditions: {
      struggle_categories: ['sleep']
    }
  },
  // Mood/mental health focused
  {
    id: 'p2-mood-mental',
    page: 2,
    content: {
      headline: 'Your Mood Swings Have a Biological Basis',
      body: "Irritability, anxiety, and mood swings aren't 'all in your head'—they're driven by fluctuating estrogen and progesterone, which affect serotonin and GABA (your brain's calming neurotransmitters). You can support your mood with targeted strategies.",
      listItems: [
        'Estrogen affects serotonin production (the "happy" neurotransmitter)',
        'Progesterone affects GABA (the "calm" neurotransmitter)',
        'Hormone fluctuations create emotional volatility',
        'Exercise, nutrition, and stress management stabilize mood'
      ]
    },
    conditions: {
      struggle_categories: ['mood']
    }
  },
  // Weight/body composition focused
  {
    id: 'p2-weight-body',
    page: 2,
    content: {
      headline: 'Weight Gain Isn\'t Inevitable',
      body: "Many women gain 5-10 pounds during menopause, especially around the midsection. This is driven by hormonal changes that affect metabolism and fat storage—but it's not irreversible. The right approach to movement and nutrition makes a difference.",
      listItems: [
        'Estrogen loss shifts fat storage to the belly',
        'Muscle loss (sarcopenia) slows metabolism',
        'Insulin resistance increases with age',
        'Strength training and protein intake counteract these changes'
      ]
    },
    conditions: {
      struggle_categories: ['weight']
    }
  },
  // Physical discomfort focused
  {
    id: 'p2-physical-discomfort',
    page: 2,
    content: {
      headline: 'Your Physical Symptoms Are Manageable',
      body: "Hot flashes, night sweats, joint pain, and headaches are among the most disruptive physical symptoms. They're caused by hormonal fluctuations affecting temperature regulation, inflammation, and pain perception—but lifestyle changes can reduce their intensity.",
      listItems: [
        'Hot flashes affect 75% of women in menopause',
        'Estrogen loss increases inflammation (joint pain)',
        'Temperature regulation is controlled by the hypothalamus',
        'Cooling strategies, anti-inflammatory foods, and movement help'
      ]
    },
    conditions: {
      struggle_categories: ['physical']
    }
  },
  // Multiple struggles (general)
  {
    id: 'p2-multiple-struggles',
    page: 2,
    content: {
      headline: 'You\'re Dealing with a Lot—Let\'s Simplify',
      body: "When multiple symptoms show up at once, it can feel overwhelming. But here's the good news: many of these struggles share common root causes. Addressing foundational areas like sleep, movement, and nutrition can improve multiple symptoms simultaneously.",
      listItems: [
        'Poor sleep amplifies fatigue, mood swings, and cravings',
        'Low muscle mass worsens weight gain and energy',
        'Chronic stress compounds every symptom',
        'Small, consistent changes create cascading improvements'
      ]
    },
    conditions: {
      has_struggles: true
    }
  }
];

// ============================================================================
// PAGE 3: WHAT YOU WANT - Templates
// ============================================================================

const PAGE3_TEMPLATES: BodyInsightTemplate[] = [
  {
    id: 'p3-lose-weight',
    page: 3,
    content: {
      headline: 'Weight Management Starts with Muscle',
      body: "Your goal is to lose weight—specifically, to reduce body fat while maintaining (or building) lean muscle. During menopause, muscle mass naturally declines, which slows metabolism. The most effective approach combines strength training with moderate calorie reduction and high protein intake.",
      listItems: [
        'Muscle burns 3x more calories than fat, even at rest',
        'Strength training 2-3x/week preserves muscle during weight loss',
        'Protein (1.2-1.6g per kg body weight) prevents muscle loss',
        'Slow, steady loss (0.5-1 lb/week) is sustainable'
      ]
    },
    conditions: {
      primary_goal: 'lose_weight'
    }
  },
  {
    id: 'p3-lose-belly-fat',
    page: 3,
    content: {
      headline: 'Belly Fat Requires a Targeted Approach',
      body: "Your goal is to reduce belly fat—the most stubborn and hormonally-driven fat during menopause. Visceral fat (deep belly fat) responds to a combination of strength training, high-intensity movement, and anti-inflammatory nutrition. Spot reduction doesn't work, but full-body fat loss does.",
      listItems: [
        'Visceral fat is metabolically active and responds to exercise',
        'Strength training + HIIT are most effective for belly fat',
        'Reducing refined carbs and sugar lowers insulin (fat storage hormone)',
        'Stress management reduces cortisol (belly fat trigger)'
      ]
    },
    conditions: {
      primary_goal: 'lose_belly_fat'
    }
  },
  {
    id: 'p3-build-strength',
    page: 3,
    content: {
      headline: 'Strength is the Foundation of Vitality',
      body: "Your goal is to build strength—one of the most powerful investments you can make during menopause. Strength training combats muscle loss, boosts metabolism, improves bone density, and enhances mood. You'll feel more capable, energized, and confident.",
      listItems: [
        'Women can build significant strength at any age',
        'Strength training 2-3x/week increases muscle mass',
        'Progressive overload (gradually increasing weight) drives results',
        'Protein + recovery are essential for muscle growth'
      ]
    },
    conditions: {
      primary_goal: 'build_strength'
    }
  },
  {
    id: 'p3-improve-energy',
    page: 3,
    content: {
      headline: 'Energy Comes from Movement, Not Rest',
      body: "Your goal is to improve energy—to feel vibrant, alert, and capable throughout the day. Paradoxically, strategic movement (not more rest) is the fastest way to boost energy. Exercise increases mitochondrial density (cellular energy factories), improves sleep, and stabilizes blood sugar.",
      listItems: [
        'Regular movement increases mitochondria (energy production)',
        'Morning movement improves focus and mood all day',
        'Strength training improves insulin sensitivity (stable energy)',
        'Quality sleep is the foundation—movement helps you sleep better'
      ]
    },
    conditions: {
      primary_goal: 'improve_energy'
    }
  }
];

// ============================================================================
// PAGE 4: YOUR FOCUS - Templates (with FocusArea data)
// ============================================================================

const FOCUS_AREAS_LOSE_WEIGHT: FocusArea[] = [
  {
    category: 'Movement',
    title: 'Build Muscle to Burn Fat',
    description: 'Strength training 2-3x per week to preserve muscle mass during weight loss.',
    why: 'Muscle burns 3x more calories than fat, even at rest. Maintaining muscle keeps your metabolism high.',
    suggestions: [
      'Full-body strength workouts (squats, lunges, push-ups, rows)',
      '8-12 reps per exercise, 2-3 sets',
      'Add moderate cardio (walking, cycling) 3-4x/week for calorie burn'
    ]
  },
  {
    category: 'Nutrition',
    title: 'High Protein, Moderate Calorie Deficit',
    description: 'Eat enough protein to preserve muscle while creating a sustainable calorie deficit.',
    why: 'Protein (1.2-1.6g per kg) prevents muscle loss during weight loss. A moderate deficit (250-500 cal/day) ensures sustainable progress.',
    suggestions: [
      'Prioritize protein at every meal (eggs, chicken, fish, Greek yogurt, legumes)',
      'Reduce refined carbs and sugar (replace with whole grains, vegetables)',
      'Stay hydrated (often mistaken for hunger)'
    ]
  },
  {
    category: 'Recovery',
    title: 'Sleep and Stress Management',
    description: 'Prioritize 7-9 hours of quality sleep and manage stress to support fat loss.',
    why: 'Poor sleep increases hunger hormones (ghrelin) and fat storage (cortisol). Chronic stress drives belly fat accumulation.',
    suggestions: [
      'Establish a consistent sleep schedule',
      'Practice stress-reduction techniques (meditation, deep breathing, walks)',
      'Limit late-night eating (finish dinner 2-3 hours before bed)'
    ]
  }
];

const FOCUS_AREAS_LOSE_BELLY_FAT: FocusArea[] = [
  {
    category: 'Movement',
    title: 'Strength + High-Intensity Intervals',
    description: 'Combine strength training with HIIT to target visceral belly fat.',
    why: 'Visceral fat responds well to high-intensity exercise. Strength training builds muscle; HIIT burns fat efficiently.',
    suggestions: [
      'Strength training 2-3x/week (compound movements: deadlifts, squats, rows)',
      'HIIT 1-2x/week (20-30 min: sprint intervals, cycling, rowing)',
      'Daily walking (10,000 steps reduces visceral fat)'
    ]
  },
  {
    category: 'Nutrition',
    title: 'Anti-Inflammatory, Low-Glycemic Eating',
    description: 'Reduce refined carbs and sugar to lower insulin and inflammation.',
    why: 'Insulin resistance drives belly fat storage. Anti-inflammatory foods reduce visceral fat accumulation.',
    suggestions: [
      'Focus on whole foods: vegetables, lean proteins, healthy fats (olive oil, nuts, fish)',
      'Reduce sugar, white bread, and processed foods',
      'Try a Mediterranean-style diet (20% lower hot flashes, supports fat loss)'
    ]
  },
  {
    category: 'Recovery',
    title: 'Stress and Cortisol Management',
    description: 'Manage chronic stress to reduce cortisol-driven belly fat.',
    why: 'Cortisol (stress hormone) specifically increases visceral fat storage around the midsection.',
    suggestions: [
      'Practice daily stress reduction (yoga, meditation, journaling)',
      'Prioritize sleep (cortisol spikes with sleep deprivation)',
      'Avoid over-exercising (can raise cortisol—balance intensity with recovery)'
    ]
  }
];

const FOCUS_AREAS_BUILD_STRENGTH: FocusArea[] = [
  {
    category: 'Movement',
    title: 'Progressive Strength Training',
    description: 'Lift weights 2-3x per week with progressive overload.',
    why: 'Muscles grow when challenged beyond their current capacity. Gradually increasing weight or reps drives strength gains.',
    suggestions: [
      'Focus on compound exercises (squats, deadlifts, bench press, rows)',
      'Aim for 6-10 reps per set (heavier weights build strength)',
      'Increase weight by 2-5% when you can complete all reps with good form'
    ]
  },
  {
    category: 'Nutrition',
    title: 'High Protein for Muscle Growth',
    description: 'Consume 1.6-2.0g protein per kg body weight to support muscle building.',
    why: 'Protein provides the building blocks (amino acids) for muscle repair and growth. Menopause increases protein needs.',
    suggestions: [
      'Eat protein at every meal (aim for 25-30g per meal)',
      'Include post-workout protein (within 2 hours of training)',
      'Choose complete proteins: meat, fish, eggs, dairy, or combine plant proteins'
    ]
  },
  {
    category: 'Recovery',
    title: 'Rest and Recovery Days',
    description: 'Allow 48 hours between strength sessions for muscle repair.',
    why: 'Muscles grow during rest, not during workouts. Overtraining prevents progress and increases injury risk.',
    suggestions: [
      'Schedule 1-2 full rest days per week',
      'Sleep 7-9 hours (muscle repair happens during deep sleep)',
      'Include active recovery (gentle yoga, walking, stretching)'
    ]
  }
];

const FOCUS_AREAS_IMPROVE_ENERGY: FocusArea[] = [
  {
    category: 'Movement',
    title: 'Morning Movement for All-Day Energy',
    description: 'Start your day with 10-20 minutes of movement to boost energy and focus.',
    why: 'Morning exercise increases mitochondrial activity, improves mood, and stabilizes blood sugar throughout the day.',
    suggestions: [
      'Try morning walks, yoga, or light strength exercises',
      'Aim for consistency over intensity (daily habit builds energy)',
      'Include 2-3 strength sessions per week to boost metabolism'
    ]
  },
  {
    category: 'Nutrition',
    title: 'Stable Blood Sugar for Steady Energy',
    description: 'Eat balanced meals with protein, fiber, and healthy fats to avoid energy crashes.',
    why: 'Blood sugar spikes and crashes cause fatigue. Balanced meals provide sustained energy.',
    suggestions: [
      'Never skip breakfast (include protein to stabilize morning energy)',
      'Avoid refined carbs and sugary snacks (cause energy crashes)',
      'Eat small, frequent meals or snacks (every 3-4 hours)'
    ]
  },
  {
    category: 'Recovery',
    title: 'Prioritize Sleep Quality',
    description: 'Improve sleep quality to wake up refreshed and energized.',
    why: 'Poor sleep is the #1 cause of daytime fatigue. Quality sleep restores energy and mental clarity.',
    suggestions: [
      'Establish a consistent sleep schedule (same bedtime/wake time)',
      'Create a cool, dark sleep environment',
      'Avoid caffeine after 2pm and screens 1 hour before bed'
    ]
  }
];

const PAGE4_TEMPLATES: BodyInsightTemplate[] = [
  {
    id: 'p4-lose-weight',
    page: 4,
    content: {
      headline: 'Your Focus: Movement, Nutrition, Recovery',
      body: 'To achieve sustainable weight loss, you\'ll focus on three interconnected areas. Each one supports the others—building muscle boosts metabolism, proper nutrition fuels workouts, and recovery ensures progress.',
      focusAreas: FOCUS_AREAS_LOSE_WEIGHT
    },
    conditions: {
      primary_goal: 'lose_weight'
    }
  },
  {
    id: 'p4-lose-belly-fat',
    page: 4,
    content: {
      headline: 'Your Focus: Movement, Nutrition, Recovery',
      body: 'To reduce belly fat, you\'ll target visceral fat through high-intensity movement, anti-inflammatory eating, and stress management. These three areas work together to reduce hormonal belly fat.',
      focusAreas: FOCUS_AREAS_LOSE_BELLY_FAT
    },
    conditions: {
      primary_goal: 'lose_belly_fat'
    }
  },
  {
    id: 'p4-build-strength',
    page: 4,
    content: {
      headline: 'Your Focus: Movement, Nutrition, Recovery',
      body: 'To build strength, you\'ll progressively challenge your muscles, fuel them with protein, and give them time to recover. Strength comes from consistency in all three areas.',
      focusAreas: FOCUS_AREAS_BUILD_STRENGTH
    },
    conditions: {
      primary_goal: 'build_strength'
    }
  },
  {
    id: 'p4-improve-energy',
    page: 4,
    content: {
      headline: 'Your Focus: Movement, Nutrition, Recovery',
      body: 'To improve energy, you\'ll use movement to boost mitochondrial function, stabilize blood sugar through nutrition, and prioritize sleep for recovery. Energy is built, not found.',
      focusAreas: FOCUS_AREAS_IMPROVE_ENERGY
    },
    conditions: {
      primary_goal: 'improve_energy'
    }
  }
];

// ============================================================================
// PAGE 5: YOUR 12-WEEK TIMELINE - Templates (with TimelinePhase data)
// ============================================================================

const TIMELINE_PHASES_LOSE_WEIGHT: TimelinePhase[] = [
  {
    phase: 1,
    weeks: 'Weeks 1-4',
    title: 'Foundation',
    focus: 'Build habits and baseline strength',
    what_to_expect: [
      'Learn proper form for strength exercises',
      'Establish a protein-rich eating pattern',
      'Initial water weight loss (2-4 lbs)',
      'Improved energy and sleep quality'
    ]
  },
  {
    phase: 2,
    weeks: 'Weeks 5-8',
    title: 'Momentum',
    focus: 'Increase intensity and consistency',
    what_to_expect: [
      'Noticeable muscle definition',
      'Steady fat loss (1-2 lbs per week)',
      'Clothes fitting differently',
      'Increased strength and stamina'
    ]
  },
  {
    phase: 3,
    weeks: 'Weeks 9-12',
    title: 'Transformation',
    focus: 'Refine habits and celebrate progress',
    what_to_expect: [
      'Visible body composition changes',
      'Sustainable habits feel automatic',
      'Total loss of 8-12 lbs (healthy pace)',
      'Confidence and energy at a high'
    ]
  }
];

const TIMELINE_PHASES_LOSE_BELLY_FAT: TimelinePhase[] = [
  {
    phase: 1,
    weeks: 'Weeks 1-4',
    title: 'Foundation',
    focus: 'Reduce inflammation and build consistency',
    what_to_expect: [
      'Reduced bloating from dietary changes',
      'Establish strength + HIIT routine',
      'Learn stress management techniques',
      'Initial reduction in waist circumference (1-2 inches)'
    ]
  },
  {
    phase: 2,
    weeks: 'Weeks 5-8',
    title: 'Momentum',
    focus: 'Target visceral fat with intensity',
    what_to_expect: [
      'Noticeable reduction in midsection',
      'Improved insulin sensitivity (stable energy)',
      'Increased strength and cardiovascular fitness',
      'Pants fitting looser around waist'
    ]
  },
  {
    phase: 3,
    weeks: 'Weeks 9-12',
    title: 'Transformation',
    focus: 'Sustain progress and refine habits',
    what_to_expect: [
      'Significant waist circumference reduction (3-4 inches)',
      'Visible abdominal definition',
      'Lower inflammation markers (less joint pain)',
      'Sustainable lifestyle habits established'
    ]
  }
];

const TIMELINE_PHASES_BUILD_STRENGTH: TimelinePhase[] = [
  {
    phase: 1,
    weeks: 'Weeks 1-4',
    title: 'Foundation',
    focus: 'Learn proper form and build neural pathways',
    what_to_expect: [
      'Rapid strength gains (neural adaptations)',
      'Muscles "wake up" and feel more engaged',
      'Initial soreness (DOMS) decreases',
      'Confidence with weights increases'
    ]
  },
  {
    phase: 2,
    weeks: 'Weeks 5-8',
    title: 'Momentum',
    focus: 'Muscle growth and progressive overload',
    what_to_expect: [
      'Visible muscle definition',
      'Strength increases on all lifts (adding weight or reps)',
      'Improved posture and stability',
      'Feeling stronger in daily activities'
    ]
  },
  {
    phase: 3,
    weeks: 'Weeks 9-12',
    title: 'Transformation',
    focus: 'Peak strength and habit solidification',
    what_to_expect: [
      'Significant strength gains (50-100% increase on some lifts)',
      'Noticeable muscle growth and tone',
      'Confidence to lift heavier weights',
      'Strength training feels like a non-negotiable habit'
    ]
  }
];

const TIMELINE_PHASES_IMPROVE_ENERGY: TimelinePhase[] = [
  {
    phase: 1,
    weeks: 'Weeks 1-4',
    title: 'Foundation',
    focus: 'Establish morning movement and sleep routine',
    what_to_expect: [
      'Morning energy improves within first week',
      'Sleep quality begins to stabilize',
      'Fewer afternoon energy crashes',
      'Mood improves with consistent movement'
    ]
  },
  {
    phase: 2,
    weeks: 'Weeks 5-8',
    title: 'Momentum',
    focus: 'Build endurance and metabolic efficiency',
    what_to_expect: [
      'Sustained energy throughout the day',
      'Increased mitochondrial density (cellular energy)',
      'Better focus and mental clarity',
      'Reduced reliance on caffeine'
    ]
  },
  {
    phase: 3,
    weeks: 'Weeks 9-12',
    title: 'Transformation',
    focus: 'Peak vitality and sustainable habits',
    what_to_expect: [
      'Consistent high energy levels',
      'Deep, restorative sleep',
      'Feeling vibrant and capable',
      'Energy-supporting habits are automatic'
    ]
  }
];

const PAGE5_TEMPLATES: BodyInsightTemplate[] = [
  {
    id: 'p5-lose-weight',
    page: 5,
    content: {
      headline: 'Your 12-Week Journey to Weight Loss',
      body: 'Here\'s what to expect over the next 12 weeks. Progress isn\'t linear—some weeks you\'ll see dramatic changes, others will feel slow. Trust the process and focus on consistency.',
      timelinePhases: TIMELINE_PHASES_LOSE_WEIGHT
    },
    conditions: {
      primary_goal: 'lose_weight'
    }
  },
  {
    id: 'p5-lose-belly-fat',
    page: 5,
    content: {
      headline: 'Your 12-Week Journey to Belly Fat Reduction',
      body: 'Here\'s what to expect over the next 12 weeks. Belly fat loss takes time—visceral fat is stubborn but responds to consistency. Celebrate non-scale victories like reduced waist circumference and improved energy.',
      timelinePhases: TIMELINE_PHASES_LOSE_BELLY_FAT
    },
    conditions: {
      primary_goal: 'lose_belly_fat'
    }
  },
  {
    id: 'p5-build-strength',
    page: 5,
    content: {
      headline: 'Your 12-Week Journey to Strength',
      body: 'Here\'s what to expect over the next 12 weeks. Strength builds progressively—early gains come from neural adaptations, later gains from muscle growth. You\'ll be amazed at how much you can lift.',
      timelinePhases: TIMELINE_PHASES_BUILD_STRENGTH
    },
    conditions: {
      primary_goal: 'build_strength'
    }
  },
  {
    id: 'p5-improve-energy',
    page: 5,
    content: {
      headline: 'Your 12-Week Journey to Vitality',
      body: 'Here\'s what to expect over the next 12 weeks. Energy improvements come quickly—many women notice a difference in the first week. Consistency compounds over time.',
      timelinePhases: TIMELINE_PHASES_IMPROVE_ENERGY
    },
    conditions: {
      primary_goal: 'improve_energy'
    }
  }
];

// ============================================================================
// TEMPLATE SELECTION LOGIC
// ============================================================================

/**
 * Select the best template for a given page based on user profile data.
 */
export function selectTemplate(
  page: 1 | 2 | 3 | 4 | 5,
  profile: UserProfile
): BodyInsightTemplate | null {
  const templates = [
    ...PAGE1_TEMPLATES,
    ...PAGE2_TEMPLATES,
    ...PAGE3_TEMPLATES,
    ...PAGE4_TEMPLATES,
    ...PAGE5_TEMPLATES
  ].filter(t => t.page === page);

  // Page-specific selection logic
  switch (page) {
    case 1:
      return selectPage1Template(templates, profile);
    case 2:
      return selectPage2Template(templates, profile);
    case 3:
      return selectPage3Template(templates, profile);
    case 4:
      return selectPage4Template(templates, profile);
    case 5:
      return selectPage5Template(templates, profile);
    default:
      return null;
  }
}

function selectPage1Template(
  templates: BodyInsightTemplate[],
  profile: UserProfile
): BodyInsightTemplate | null {
  const lifeStage = profile.life_stage || 'perimenopause';
  const hasStruggles = profile.struggles ? profile.struggles.length > 0 : false;

  return templates.find(t =>
    t.conditions.life_stage === lifeStage &&
    t.conditions.has_struggles === hasStruggles
  ) || templates[0];
}

function selectPage2Template(
  templates: BodyInsightTemplate[],
  profile: UserProfile
): BodyInsightTemplate | null {
  const struggles = profile.struggles || [];

  if (struggles.length === 0) {
    // Fallback to general template
    return templates.find(t => t.conditions.has_struggles === true) || templates[0];
  }

  // Categorize struggles
  const energyStruggles = ['fatigue', 'low_energy'];
  const sleepStruggles = ['insomnia', 'night_sweats', 'sleep_disruption'];
  const moodStruggles = ['mood_swings', 'irritability', 'anxiety', 'depression'];
  const weightStruggles = ['weight_gain', 'belly_fat'];
  const physicalStruggles = ['hot_flashes', 'joint_pain', 'headaches'];

  // Count struggles per category
  const struggleCounts = {
    energy: struggles.filter((s: string) => energyStruggles.includes(s)).length,
    sleep: struggles.filter((s: string) => sleepStruggles.includes(s)).length,
    mood: struggles.filter((s: string) => moodStruggles.includes(s)).length,
    weight: struggles.filter((s: string) => weightStruggles.includes(s)).length,
    physical: struggles.filter((s: string) => physicalStruggles.includes(s)).length
  };

  // Find dominant category
  const dominantCategory = Object.entries(struggleCounts)
    .sort(([, a], [, b]) => b - a)[0][0];

  // If multiple categories are tied, use general template
  if (Object.values(struggleCounts).filter(count => count > 0).length > 2) {
    return templates.find(t => t.id === 'p2-multiple-struggles') || templates[0];
  }

  // Select template based on dominant category
  const categoryTemplateMap: { [key: string]: string } = {
    energy: 'p2-energy-fatigue',
    sleep: 'p2-sleep-disruption',
    mood: 'p2-mood-mental',
    weight: 'p2-weight-body',
    physical: 'p2-physical-discomfort'
  };

  return templates.find(t => t.id === categoryTemplateMap[dominantCategory]) || templates[0];
}

function selectPage3Template(
  templates: BodyInsightTemplate[],
  profile: UserProfile
): BodyInsightTemplate | null {
  const primaryGoal = profile.primary_goal || 'improve_energy';
  return templates.find(t => t.conditions.primary_goal === primaryGoal) || templates[0];
}

function selectPage4Template(
  templates: BodyInsightTemplate[],
  profile: UserProfile
): BodyInsightTemplate | null {
  const primaryGoal = profile.primary_goal || 'improve_energy';
  return templates.find(t => t.conditions.primary_goal === primaryGoal) || templates[0];
}

function selectPage5Template(
  templates: BodyInsightTemplate[],
  profile: UserProfile
): BodyInsightTemplate | null {
  const primaryGoal = profile.primary_goal || 'improve_energy';
  return templates.find(t => t.conditions.primary_goal === primaryGoal) || templates[0];
}

/**
 * Get personalized content for a specific page.
 * Returns null if template selection fails.
 */
export function getPageContent(
  page: 1 | 2 | 3 | 4 | 5,
  profile: UserProfile
): BodyInsightTemplate['content'] | null {
  const template = selectTemplate(page, profile);
  return template ? template.content : null;
}
